import React, { useState, useEffect } from 'react';
import { LeetCodePreferences } from '../utils/leetCodeUtils';
import { useMembership } from '../contexts/MembershipContext';

interface StructuredPreferences {
  difficulty: string;
  area: string;
  companyStyle: string;
}

interface FreeFormPreferences {
  freeFormText: string;
}

type Preferences =
  | StructuredPreferences
  | FreeFormPreferences
  | LeetCodePreferences;

interface PreferencesModalProps {
  onConfirm: (
    preferences: Preferences,
    isStructured: boolean,
    isLeetCode?: boolean
  ) => void;
  onCancel: () => void;
}

const PreferencesModal: React.FC<PreferencesModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const { membershipInfo, loading: membershipLoading } = useMembership();
  const isStarterPlan = membershipInfo?.tier === 1;
  const [activeTab, setActiveTab] = useState<
    'structured' | 'freeform' | 'leetcode'
  >('structured');
  const [difficulty, setDifficulty] = useState('');

  // Set default difficulty to Easy for starter plan users
  useEffect(() => {
    if (!membershipLoading && isStarterPlan) {
      setDifficulty('Easy');
    }
  }, [isStarterPlan, difficulty, membershipLoading]);
  const [area, setArea] = useState('');
  const [companyStyle, setCompanyStyle] = useState('');
  const [freeFormText, setFreeFormText] = useState('');

  // LeetCode form states
  const [leetCodeTitle, setLeetCodeTitle] = useState('');
  const [leetCodeDifficulty, setLeetCodeDifficulty] = useState('');
  const [leetCodeDescription, setLeetCodeDescription] = useState('');
  const [leetCodeExamples, setLeetCodeExamples] = useState('');
  const [leetCodeConstraints, setLeetCodeConstraints] = useState('');

  const difficultyOptions = [
    { value: '', label: 'Surprise me' },
    { value: 'Easy', label: 'Easy' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Hard', label: 'Hard' },
  ];

  const areaOptions = [
    { value: '', label: "I'm ready with anything" },
    { value: 'Arrays & Strings', label: 'Arrays & Strings' },
    { value: 'Trees & Graphs', label: 'Trees & Graphs' },
    { value: 'Dynamic Programming', label: 'Dynamic Programming' },
    {
      value: 'Data Structures',
      label: 'Data Structures (Stack, Queue, Hash Table)',
    },
    { value: 'Algorithms', label: 'Algorithms (Binary Search, Two Pointers)' },
  ];

  const companyStyleOptions = [
    {
      value: '',
      label: 'No specific style preference',
      description: 'Standard interview questions',
    },
    {
      value: 'Meta',
      label: 'Meta (Facebook)',
      description: 'Graphs, trees, recursion, sliding window',
    },
    {
      value: 'Google',
      label: 'Google',
      description: 'Complex algorithms, system design thinking',
    },
    {
      value: 'Amazon',
      label: 'Amazon',
      description: 'Arrays, strings, leadership principles',
    },
    {
      value: 'Microsoft',
      label: 'Microsoft',
      description: 'Trees, graphs, object-oriented design',
    },
    {
      value: 'Apple',
      label: 'Apple',
      description: 'System design, optimization, clean code',
    },
  ];

  const handleConfirm = () => {
    if (activeTab === 'structured') {
      onConfirm({ difficulty, area, companyStyle }, true);
    } else if (activeTab === 'leetcode') {
      onConfirm(
        {
          title: leetCodeTitle,
          difficulty: leetCodeDifficulty,
          description: leetCodeDescription,
          examples: leetCodeExamples,
          constraints: leetCodeConstraints,
        },
        false,
        true
      );
    } else {
      onConfirm({ freeFormText }, false);
    }
  };

  const isFormValid = () => {
    if (activeTab === 'structured') {
      return true; // Allow empty values for "Surprise me" options
    } else if (activeTab === 'leetcode') {
      return (
        leetCodeTitle.trim().length > 0 &&
        leetCodeDifficulty.trim().length > 0 &&
        leetCodeDescription.trim().length > 0 &&
        leetCodeExamples.trim().length > 0 &&
        leetCodeConstraints.trim().length > 0
      );
    } else {
      return freeFormText.trim().length > 0;
    }
  };

  return (
    <div
      className={
        'absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
      }
    >
      <div className='bg-white rounded-lg p-8 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto'>
        <h2 className='text-2xl font-bold text-gray-800 mb-6 text-center'>
          Interview Preferences
        </h2>

        {/* Starter Plan Banner */}
        {isStarterPlan && (
          <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
            <div className='flex items-center'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-blue-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <p className='text-sm text-blue-700'>
                  <strong>Starter Plan:</strong> You can only practice easy
                  questions with structured requests.{' '}
                  <a
                    href='/profile/subscription'
                    className='underline hover:text-blue-800'
                  >
                    Upgrade your plan
                  </a>{' '}
                  for more features.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className='flex border-b border-gray-200 mb-6'>
          <button
            onClick={() => setActiveTab('structured')}
            className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'structured'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Structured Request
          </button>
          <button
            onClick={() => setActiveTab('freeform')}
            disabled={isStarterPlan}
            className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              isStarterPlan
                ? 'border-transparent text-gray-400 cursor-not-allowed'
                : activeTab === 'freeform'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Custom Request
          </button>
          <button
            onClick={() => setActiveTab('leetcode')}
            disabled={isStarterPlan}
            className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              isStarterPlan
                ? 'border-transparent text-gray-400 cursor-not-allowed'
                : activeTab === 'leetcode'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            LeetCode Request
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'structured' ? (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Difficulty Level
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                disabled={isStarterPlan}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isStarterPlan ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                {difficultyOptions.map((option) => {
                  // For starter plan, only show Easy option
                  if (
                    isStarterPlan &&
                    option.value !== 'Easy' &&
                    option.value !== ''
                  ) {
                    return null;
                  }
                  return (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Problem Area
              </label>
              <select
                value={area}
                onChange={(e) => setArea(e.target.value)}
                disabled={isStarterPlan}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isStarterPlan ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                {areaOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Company Question Style
              </label>
              <div className='space-y-2'>
                {companyStyleOptions.map((option) => (
                  <div
                    key={option.value}
                    className='flex items-center'
                  >
                    <input
                      type='radio'
                      id={`company-${option.value}`}
                      name='companyStyle'
                      value={option.value}
                      checked={companyStyle === option.value}
                      onChange={(e) => setCompanyStyle(e.target.value)}
                      disabled={isStarterPlan}
                      className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 ${
                        isStarterPlan ? 'cursor-not-allowed' : ''
                      }`}
                    />
                    <label
                      htmlFor={`company-${option.value}`}
                      className='ml-3 text-sm text-gray-700 cursor-pointer flex items-center'
                    >
                      {option.label}
                      <div className='group relative ml-2'>
                        <div className='h-4 w-4 border border-gray-400 rounded-full text-gray-600 text-xs flex items-center justify-center cursor-help bg-transparent hover:border-gray-600 transition-colors'>
                          i
                        </div>
                        <div className='absolute left-0 bottom-6 transform translate-x-0 w-64 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10'>
                          {option.description}
                          <div className='absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800'></div>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : activeTab === 'leetcode' ? (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Problem Title *
              </label>
              <input
                type='text'
                value={leetCodeTitle}
                onChange={(e) => setLeetCodeTitle(e.target.value)}
                placeholder='e.g., Two Sum, Valid Parentheses, etc.'
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Difficulty *
              </label>
              <select
                value={leetCodeDifficulty}
                onChange={(e) => setLeetCodeDifficulty(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              >
                <option value=''>Select difficulty</option>
                <option value='Easy'>Easy</option>
                <option value='Medium'>Medium</option>
                <option value='Hard'>Hard</option>
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Problem Description *
              </label>
              <textarea
                value={leetCodeDescription}
                onChange={(e) => setLeetCodeDescription(e.target.value)}
                placeholder='Enter the full problem description...'
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical'
                rows={6}
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Examples *
              </label>
              <textarea
                value={leetCodeExamples}
                onChange={(e) => setLeetCodeExamples(e.target.value)}
                placeholder='Example 1:&#10;Input: nums = [2,7,11,15], target = 9&#10;Output: [0,1]&#10;Explanation: Because nums[0] + nums[1] == 9, we return [0, 1].'
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical'
                rows={4}
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Constraints *
              </label>
              <textarea
                value={leetCodeConstraints}
                onChange={(e) => setLeetCodeConstraints(e.target.value)}
                placeholder='2 ≤ nums.length ≤ 10⁴&#10;-10⁹ ≤ nums[i] ≤ 10⁹&#10;-10⁹ ≤ target ≤ 10⁹&#10;Only one valid answer exists.'
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical'
                rows={3}
              />
            </div>
          </div>
        ) : (
          <div className='space-y-6'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Describe the type of coding problem you'd like to practice.
              </label>
              <textarea
                value={freeFormText}
                onChange={(e) => setFreeFormText(e.target.value)}
                placeholder='Example: I want to practice a medium-difficulty tree traversal problem that focuses on recursion and is similar to questions asked at Google.'
                className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical'
                rows={6}
              />
              <p className='text-xs text-gray-500 mt-2'>
                Be as specific as possible about difficulty, topic, company
                style, or any other requirements you have. You can also ask for
                a specific problem by its title, e.g. "Two Sum".
              </p>
            </div>
          </div>
        )}

        <div className='mt-8 flex justify-center space-x-4'>
          <button
            onClick={onCancel}
            className='px-6 py-3 rounded-lg transition-colors font-medium text-lg border border-gray-300 text-gray-700 hover:bg-gray-50'
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isFormValid()}
            className={`px-8 py-3 rounded-lg transition-colors font-medium text-lg ${
              isFormValid()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Start Interview
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesModal;
